import { useEffect, useState } from "react";
import { useLang } from "../../context/LanguageContext";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { CalendarDays, Users, BedDouble, CreditCard, ExternalLink, Info, ArrowLeft, Loader2 } from "lucide-react";
import { MS_PER_DAY } from "../../lib/constants";
import AvailabilityCalendar from "./AvailabilityCalendar";
import NumberStepper from "./NumberStepper";
import {
  trackViewItem,
  trackQuoteCalculated,
  trackBeginCheckout,
} from "../../lib/analytics";

export default function BookingEngine({ initialApartment }) {
  const { lang, t } = useLang();
  const [apartments, setApartments] = useState([]);
  const [aptId, setAptId] = useState(initialApartment || "");
  const [checkin, setCheckin] = useState("");
  const [checkout, setCheckout] = useState("");
  const [adults, setAdults] = useState(2);
  const [children, setChildren] = useState(0);
  const [quote, setQuote] = useState(null);
  const [step, setStep] = useState(1);
  const [guest, setGuest] = useState({ name: "", email: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [stripeUrl, setStripeUrl] = useState("");

  useEffect(() => {
    api.get("/apartments").then((r) => {
      setApartments(r.data);
      setAptId((cur) => cur || r.data[0]?.id || "");
    });
  }, []);

  useEffect(() => {
    if (initialApartment) {
      setAptId(initialApartment);
      setStep(1);
    }
  }, [initialApartment]);

  const today = new Date().toISOString().slice(0, 10);
  const tomorrow = new Date(Date.now() + MS_PER_DAY).toISOString().slice(0, 10);

  // Range picker state
  const fmt = (d) => (d ? new Date(d).toISOString().slice(0, 10) : "");
  const dateRange = checkin && checkout
    ? { from: new Date(checkin), to: new Date(checkout) }
    : checkin
      ? { from: new Date(checkin) }
      : undefined;
  const handleRangeChange = (r) => {
    setCheckin(fmt(r?.from));
    setCheckout(fmt(r?.to));
    setQuote(null);
  };

  const calcQuote = async () => {
    if (!aptId || !checkin || !checkout) {
      toast.error(lang === "el" ? "Συμπληρώστε τα πεδία" : "Please fill all fields");
      return;
    }
    setLoading(true);
    try {
      const r = await api.post("/quote", {
        apartment_id: aptId,
        check_in: checkin,
        check_out: checkout,
      });
      setQuote(r.data);
      if (!r.data.available) {
        toast.error(t.booking.unavailable);
      } else if (r.data.meets_min_nights === false) {
        toast.error(
          lang === "el"
            ? `Ελάχιστη διαμονή: ${r.data.min_nights} νύχτες`
            : `Minimum stay: ${r.data.min_nights} nights`
        );
      } else {
        const apt = apartments.find((a) => a.id === aptId);
        trackQuoteCalculated({ apt, quote: r.data });
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally {
      setLoading(false);
    }
  };

  const proceedToPayment = async () => {
    if (!guest.name || !guest.email || !guest.phone) {
      toast.error(lang === "el" ? "Συμπληρώστε τα στοιχεία σας" : "Please complete your details");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(guest.email)) {
      toast.error(lang === "el" ? "Μη έγκυρη διεύθυνση email" : "Invalid email address");
      return;
    }
    setStep(3); // advance stepper visually so user sees progress
    setLoading(true);
    try {
      const b = await api.post("/bookings", {
        apartment_id: aptId,
        check_in: checkin,
        check_out: checkout,
        adults,
        children,
        guest_name: guest.name,
        guest_email: guest.email,
        guest_phone: guest.phone,
        message: guest.message,
      });
      const s = await api.post("/checkout/session", {
        booking_id: b.data.id,
        origin_url: window.location.origin,
      });
      const apt = apartments.find((a) => a.id === aptId);
      trackBeginCheckout({ apt, quote, bookingId: b.data.id });
      setStripeUrl(s.data.url);
      window.location.href = s.data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error creating booking");
      setStep(2); // rollback stepper on error
      setLoading(false);
    }
  };

  const selectedApt = apartments.find((a) => a.id === aptId);

  // GA4: view_item whenever apartment selection changes (and apt is loaded)
  useEffect(() => {
    if (selectedApt) trackViewItem(selectedApt);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aptId, apartments.length]);

  return (
    <section
      id="booking"
      data-testid="booking-section"
      className="py-20 sm:py-32 bg-white overflow-x-hidden"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mb-10 sm:mb-12">
          <p className="uppercase text-xs sm:text-sm tracking-[0.25em] text-[#6B705C] mb-3 sm:mb-4">
            {t.booking.overline}
          </p>
          <h2 className="font-serif text-3xl sm:text-5xl lg:text-6xl text-[#1A2B4C] tracking-tight leading-[1.05] mb-3 sm:mb-4">
            {t.booking.title}
          </h2>
          <p className="text-gray-600 text-base sm:text-lg">{t.booking.subtitle}</p>
        </div>

        <div className="grid lg:grid-cols-12 gap-6 lg:gap-10">
          {/* Booking card */}
          <div className="lg:col-span-8">
            <div
              data-testid="booking-card"
              className="bg-[#F5F2EB] rounded-2xl sm:rounded-3xl p-4 sm:p-10 border border-[#E9E2D3] shadow-sm"
            >
              {/* Stepper */}
              <div className="flex items-center gap-1 sm:gap-2 mb-6 sm:mb-8 text-[10px] sm:text-xs uppercase tracking-widest overflow-x-auto no-scrollbar">
                {[t.booking.step1, t.booking.step2, t.booking.step3].map(
                  (label, i) => (
                    <div key={label} className="flex items-center gap-1.5 sm:gap-2 shrink-0">
                      <span
                        className={`w-5 h-5 sm:w-6 sm:h-6 text-[10px] sm:text-xs flex items-center justify-center rounded-full ${
                          step > i
                            ? "bg-[#1A2B4C] text-white"
                            : step === i + 1
                              ? "bg-[#1A2B4C] text-white"
                              : "bg-white text-[#6B705C] border border-[#E9E2D3]"
                        }`}
                      >
                        {i + 1}
                      </span>
                      <span className={step >= i + 1 ? "text-[#1A2B4C]" : "text-[#6B705C]"}>
                        {label}
                      </span>
                      {i < 2 && <span className="text-[#6B705C] mx-0.5 sm:mx-1">—</span>}
                    </div>
                  )
                )}
              </div>

              {step === 1 && (
                <div className="space-y-5">
                  <div>
                    <label className="text-xs uppercase tracking-widest text-[#6B705C] mb-2 block">
                      {t.booking.apartment}
                    </label>
                    <select
                      value={aptId}
                      onChange={(e) => setAptId(e.target.value)}
                      data-testid="booking-apartment-select"
                      className="w-full bg-white border border-[#E9E2D3] rounded-xl px-4 py-3.5 text-[#1A2B4C] focus:outline-none focus:ring-2 focus:ring-[#1A2B4C]/30"
                    >
                      {apartments.map((a) => (
                        <option key={a.id} value={a.id}>
                          {lang === "el" ? a.name_el : a.name_en} — €{a.price_per_night}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="text-xs uppercase tracking-widest text-[#6B705C] mb-2 block">
                      <CalendarDays className="inline w-3.5 h-3.5 mr-1" />
                      {t.booking.pickDates || "Pick dates"}
                    </label>
                    <AvailabilityCalendar
                      aptId={aptId}
                      range={dateRange}
                      onRangeChange={handleRangeChange}
                      lang={lang}
                      t={t}
                    />
                    {/* Hidden inputs kept for test selectors / a11y */}
                    <input type="hidden" data-testid="booking-checkin-input" value={checkin} readOnly />
                    <input type="hidden" data-testid="booking-checkout-input" value={checkout} readOnly />
                  </div>

                  <div className="grid sm:grid-cols-2 gap-5">
                    <div>
                      <label className="text-xs uppercase tracking-widest text-[#6B705C] mb-2 block">
                        <Users className="inline w-3.5 h-3.5 mr-1" />
                        {t.booking.adults}
                      </label>
                      <NumberStepper
                        value={adults}
                        onChange={setAdults}
                        min={1}
                        max={8}
                        label={t.booking.adults}
                        testid="booking-adults-input"
                      />
                    </div>
                    <div>
                      <label className="text-xs uppercase tracking-widest text-[#6B705C] mb-2 block">
                        <BedDouble className="inline w-3.5 h-3.5 mr-1" />
                        {t.booking.children}
                      </label>
                      <NumberStepper
                        value={children}
                        onChange={setChildren}
                        min={0}
                        max={6}
                        label={t.booking.children}
                        testid="booking-children-input"
                      />
                    </div>
                  </div>

                  <button
                    onClick={calcQuote}
                    disabled={loading}
                    data-testid="booking-calculate-btn"
                    className="w-full bg-[#1A2B4C] text-white py-4 rounded-full text-sm tracking-widest uppercase hover:-translate-y-0.5 transition-transform disabled:opacity-50"
                  >
                    {loading ? t.booking.processing : t.booking.checkPrice}
                  </button>

                  {quote && quote.available && quote.meets_min_nights !== false && (
                    <button
                      onClick={() => setStep(2)}
                      data-testid="booking-continue-btn"
                      className="w-full border border-[#1A2B4C] text-[#1A2B4C] py-4 rounded-full text-sm tracking-widest uppercase hover:bg-[#1A2B4C] hover:text-white transition-all"
                    >
                      {t.booking.continueToGuest}
                    </button>
                  )}

                  {quote && quote.available && quote.meets_min_nights === false && (
                    <div
                      data-testid="booking-min-nights-warning"
                      className="w-full text-center bg-amber-50 border border-amber-200 text-amber-900 rounded-2xl px-4 py-3 text-sm"
                    >
                      {lang === "el"
                        ? `Ελάχιστη διαμονή: ${quote.min_nights} νύχτες. Παρακαλούμε επεκτείνετε τις ημερομηνίες σας.`
                        : `Minimum stay: ${quote.min_nights} nights. Please extend your dates.`}
                    </div>
                  )}
                </div>
              )}

              {step === 3 && (
                <div data-testid="booking-step3" className="text-center py-10 space-y-5">
                  <Loader2 className="w-10 h-10 text-[#1A2B4C] animate-spin mx-auto" />
                  <p className="text-[#1A2B4C] font-serif text-xl">
                    {lang === "el"
                      ? "Σας ανακατευθύνουμε στην ασφαλή πληρωμή…"
                      : "Redirecting you to secure payment…"}
                  </p>
                  <p className="text-[#6B705C] text-sm max-w-md mx-auto">
                    {lang === "el"
                      ? "Αν δεν μεταφερθείτε αυτόματα μέσα σε λίγα δευτερόλεπτα, πατήστε το παρακάτω κουμπί:"
                      : "If you are not redirected automatically within a few seconds, click the button below:"}
                  </p>
                  {stripeUrl && (
                    <a
                      href={stripeUrl}
                      data-testid="booking-stripe-fallback"
                      className="inline-flex items-center gap-2 bg-[#1A2B4C] text-white px-6 py-3 rounded-full text-sm tracking-widest uppercase hover:-translate-y-0.5 transition-transform"
                    >
                      <CreditCard className="w-4 h-4" />
                      {t.booking.payNow}
                    </a>
                  )}
                </div>
              )}


              {step === 2 && (
                <div className="space-y-5">
                  <div className="grid sm:grid-cols-2 gap-5">
                    <input
                      placeholder={t.booking.fullName}
                      value={guest.name}
                      onChange={(e) => setGuest({ ...guest, name: e.target.value })}
                      data-testid="booking-guest-name"
                      className="w-full bg-white border border-[#E9E2D3] rounded-xl px-4 py-3.5 text-[#1A2B4C] focus:outline-none focus:ring-2 focus:ring-[#1A2B4C]/30"
                    />
                    <div>
                      <input
                        placeholder={t.booking.email}
                        type="email"
                        value={guest.email}
                        onChange={(e) => setGuest({ ...guest, email: e.target.value })}
                        data-testid="booking-guest-email"
                        aria-invalid={
                          guest.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(guest.email)
                            ? "true"
                            : "false"
                        }
                        className={`w-full bg-white border rounded-xl px-4 py-3.5 text-[#1A2B4C] focus:outline-none focus:ring-2 ${
                          guest.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(guest.email)
                            ? "border-red-300 focus:ring-red-200"
                            : "border-[#E9E2D3] focus:ring-[#1A2B4C]/30"
                        }`}
                      />
                      {guest.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(guest.email) && (
                        <p className="text-xs text-red-600 mt-1.5">
                          {lang === "el"
                            ? "Μη έγκυρη διεύθυνση email"
                            : "Invalid email address"}
                        </p>
                      )}
                    </div>
                  </div>
                  <input
                    placeholder={t.booking.phone}
                    value={guest.phone}
                    onChange={(e) => setGuest({ ...guest, phone: e.target.value })}
                    data-testid="booking-guest-phone"
                    className="w-full bg-white border border-[#E9E2D3] rounded-xl px-4 py-3.5 text-[#1A2B4C] focus:outline-none focus:ring-2 focus:ring-[#1A2B4C]/30"
                  />
                  <textarea
                    placeholder={t.booking.messageOpt}
                    rows="3"
                    value={guest.message}
                    onChange={(e) => setGuest({ ...guest, message: e.target.value })}
                    data-testid="booking-guest-message"
                    className="w-full bg-white border border-[#E9E2D3] rounded-xl px-4 py-3.5 text-[#1A2B4C] focus:outline-none focus:ring-2 focus:ring-[#1A2B4C]/30"
                  />
                  <div className="flex flex-col sm:flex-row gap-3">
                    <button
                      onClick={() => setStep(1)}
                      className="border border-[#6B705C] text-[#6B705C] px-6 py-4 rounded-full text-sm tracking-widest uppercase hover:bg-[#6B705C] hover:text-white transition-all"
                    >
                      {t.booking.back}
                    </button>
                    <button
                      onClick={proceedToPayment}
                      disabled={loading}
                      data-testid="booking-pay-btn"
                      className="flex-1 bg-[#1A2B4C] text-white py-4 rounded-full text-sm tracking-widest uppercase hover:-translate-y-0.5 transition-transform disabled:opacity-50 inline-flex items-center justify-center gap-2"
                    >
                      <CreditCard className={`w-4 h-4 ${loading ? "hidden" : ""}`} />
                      {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                      {loading ? t.booking.processing : t.booking.payNow}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Summary */}
          <div className="lg:col-span-4">
            <div
              data-testid="booking-summary"
              className="bg-[#1A2B4C] text-white rounded-2xl sm:rounded-3xl p-5 sm:p-8 lg:sticky lg:top-28"
            >
              {selectedApt && (
                <>
                  <p className="uppercase text-xs tracking-widest text-white/60 mb-2">
                    {t.booking.apartment}
                  </p>
                  <h3 className="font-serif text-xl sm:text-2xl mb-4 sm:mb-6">
                    {lang === "el" ? selectedApt.name_el : selectedApt.name_en}
                  </h3>
                  <img
                    src={selectedApt.images[0]}
                    alt="apt"
                    className="rounded-2xl h-32 sm:h-40 w-full object-cover mb-4 sm:mb-6"
                  />
                </>
              )}

              {quote ? (
                <div className="space-y-3 text-sm">
                  {(() => {
                    // Group consecutive nights by rate
                    const groups = [];
                    (quote.breakdown || []).forEach((b) => {
                      const last = groups[groups.length - 1];
                      if (last && last.rate === b.rate) {
                        last.count += 1;
                      } else {
                        groups.push({ rate: b.rate, count: 1 });
                      }
                    });
                    if (groups.length <= 1) {
                      return (
                        <div className="flex justify-between gap-2">
                          <span className="text-white/70">
                            €{quote.price_per_night} × {quote.nights} {t.booking.nights}
                          </span>
                          <span className="shrink-0">€{quote.subtotal}</span>
                        </div>
                      );
                    }
                    return groups.map((g, i) => (
                      <div key={i} className="flex justify-between gap-2">
                        <span className="text-white/70">
                          €{g.rate} × {g.count} {g.count === 1 ? t.booking.night : t.booking.nights}
                        </span>
                        <span className="shrink-0">€{(g.rate * g.count).toFixed(2)}</span>
                      </div>
                    ));
                  })()}
                  {quote.short_stay_surcharge > 0 && (
                    <div
                      className="flex justify-between gap-2"
                      data-testid="booking-short-stay-surcharge"
                    >
                      <span className="text-amber-300/90 text-xs sm:text-sm">
                        {t.booking.shortStaySurcharge || "Short-stay surcharge (+20%)"}
                      </span>
                      <span className="shrink-0 text-amber-300">
                        €{quote.short_stay_surcharge}
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between gap-2">
                    <span className="text-white/70">{t.booking.cleaning}</span>
                    <span className="shrink-0">€{quote.cleaning_fee}</span>
                  </div>
                  <div className="flex justify-between gap-2">
                    <span className="text-white/70 inline-flex items-center gap-1.5">
                      {t.booking.tax}
                      <span
                        className="relative group cursor-help"
                        tabIndex={0}
                        data-testid="climate-fee-tooltip"
                      >
                        <Info className="w-3 h-3 text-white/50 hover:text-white transition-colors" />
                        <span
                          role="tooltip"
                          className="invisible opacity-0 group-hover:visible group-hover:opacity-100 group-focus:visible group-focus:opacity-100 transition-opacity duration-200 absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 sm:w-72 p-3 bg-white text-[#1A2B4C] text-[11px] leading-relaxed rounded-lg shadow-2xl z-20 normal-case"
                        >
                          {t.booking.taxTooltip || "Greek state-mandated fee (Law 5073/2023)."}
                          <span className="absolute top-full left-1/2 -translate-x-1/2 -mt-px border-4 border-transparent border-t-white" />
                        </span>
                      </span>
                    </span>
                    <span className="shrink-0">€{quote.tourist_tax}</span>
                  </div>
                  <div className="border-t border-white/20 mt-4 pt-4 flex justify-between gap-2 text-lg">
                    <span className="font-serif">{t.booking.total}</span>
                    <span data-testid="booking-total" className="font-serif shrink-0">
                      €{quote.total}
                    </span>
                  </div>
                  {quote.short_stay_surcharge > 0 && (
                    <p className="text-[11px] text-amber-300/80 leading-relaxed pt-1">
                      {t.booking.shortStayNotice || "Stays under 3 nights include a +20% surcharge"}
                    </p>
                  )}
                </div>
              ) : (
                <p className="text-white/60 text-sm">
                  {lang === "el"
                    ? "Επιλέξτε ημερομηνίες για να δείτε την τιμή."
                    : "Select dates to see the price."}
                </p>
              )}

              <div className="mt-8 pt-6 border-t border-white/20">
                <p className="text-xs uppercase tracking-widest text-white/60 mb-3">
                  {t.booking.orBookOn}
                </p>
                <div className="flex flex-col gap-2">
                  {selectedApt?.booking_url && (
                    <a
                      href={selectedApt.booking_url}
                      target="_blank"
                      rel="noreferrer"
                      data-testid="summary-booking-link"
                      className="inline-flex items-center justify-between bg-white/10 hover:bg-white/20 rounded-full px-4 py-2.5 text-sm transition-colors"
                    >
                      Booking.com <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                  )}
                  {selectedApt?.airbnb_url && (
                    <a
                      href={selectedApt.airbnb_url}
                      target="_blank"
                      rel="noreferrer"
                      data-testid="summary-airbnb-link"
                      className="inline-flex items-center justify-between bg-white/10 hover:bg-white/20 rounded-full px-4 py-2.5 text-sm transition-colors"
                    >
                      Airbnb <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
